/**
 * The neural field's geometry, generated once from a fixed seed so the mesh is
 * the same on every render and every machine. Nodes sit on a jittered lattice
 * clipped to a brain silhouette, which is why no outline is ever drawn: the
 * shape comes from where points are allowed to sit.
 *
 * Ported from the prototype's `brain.css` companion in `build.jsx`. Decorative
 * throughout — nothing here is read by a screen reader or asserted on.
 */

interface FieldNode {
  x: number;
  y: number;
  /** 0 cerebrum, 1 cerebellum, 2 brainstem. */
  region: 0 | 1 | 2;
  /** Depth plane, 0 front to 2 back. */
  z: number;
}

type FieldLink = [from: number, to: number, plane: number];

interface Field {
  nodes: FieldNode[];
  links: FieldLink[];
  /** The fifteen neurons that light as the run learns. */
  factNodes: number[];
}

const CEREBRUM: Array<[number, number]> = [
  [44, 128], [32, 110], [26, 90], [30, 68], [42, 50], [60, 36], [82, 27], [106, 23], [130, 25], [152, 33],
  [168, 46], [178, 64], [182, 86], [178, 106], [166, 120], [146, 126], [126, 128], [108, 134], [88, 138], [66, 136],
];

export const SULCI = [
  'M58 120 C 82 108, 104 100, 136 94',
  'M112 28 C 106 54, 100 80, 98 108',
  'M150 40 C 140 58, 136 76, 138 96',
];

/** The three depth planes the mesh is split across, for parallax on the turn. */
export const PLANES = [
  { z: 26, r: 1.5, name: 'front' },
  { z: 0, r: 1.15, name: 'mid' },
  { z: -26, r: 0.85, name: 'back' },
] as const;

export const FACT_NEURONS = 15;

function inPolygon(poly: Array<[number, number]>, px: number, py: number): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

const inCerebellum = (x: number, y: number) => ((x - 150) / 28) ** 2 + ((y - 144) / 17) ** 2 <= 1;

function buildField(): Field {
  let seed = 20260918;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };

  const points: Array<{ x: number; y: number; region: 0 | 1 | 2 }> = [];
  const farFrom = (px: number, py: number, d: number) =>
    points.every(n => Math.hypot(n.x - px, n.y - py) >= d);
  const add = (x: number, y: number, region: 0 | 1 | 2) => points.push({ x, y, region });

  // The cerebrum contour first, so the lobes read even before the fill.
  const SPACING = 12;
  for (let i = 0; i < CEREBRUM.length; i++) {
    const [x1, y1] = CEREBRUM[i];
    const [x2, y2] = CEREBRUM[(i + 1) % CEREBRUM.length];
    const steps = Math.max(1, Math.round(Math.hypot(x2 - x1, y2 - y1) / SPACING));
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      const px = x1 + (x2 - x1) * t;
      const py = y1 + (y2 - y1) * t;
      if (farFrom(px, py, SPACING * 0.8)) add(px, py, 0);
    }
  }
  for (let t = 0; t < 9000 && points.length < 88; t++) {
    const px = 26 + random() * 158;
    const py = 22 + random() * 118;
    if (inPolygon(CEREBRUM, px, py) && farFrom(px, py, 13)) add(px, py, 0);
  }
  // The cerebellum takes a finer grain than the cerebrum.
  for (let t = 0; t < 4000; t++) {
    const px = 122 + random() * 56;
    const py = 127 + random() * 34;
    if (inCerebellum(px, py) && farFrom(px, py, 8.5)) add(px, py, 1);
  }
  // The brainstem is a short column.
  for (let y = 136; y <= 178; y += 9) add(123 + (y - 136) * 0.04, y, 2);

  const nodes: FieldNode[] = points.map((p, i) => ({ x: p.x, y: p.y, region: p.region, z: (i * 5) % 3 }));

  const links: FieldLink[] = [];
  for (let plane = 0; plane < PLANES.length; plane++) {
    const seen = new Set<string>();
    nodes.forEach((n, i) => {
      if (n.z !== plane) return;
      nodes
        .map((m, j) => [j, Math.hypot(m.x - n.x, m.y - n.y)] as const)
        .filter(([j, d]) =>
          j !== i &&
          nodes[j].z === plane &&
          d < (n.region === 1 ? 26 : 40) &&
          (nodes[j].region === n.region || nodes[j].region === 2 || n.region === 2),
        )
        .sort((a, b) => a[1] - b[1])
        .slice(0, 3)
        .forEach(([j]) => {
          const key = i < j ? `${i}:${j}` : `${j}:${i}`;
          if (seen.has(key)) return;
          seen.add(key);
          links.push([i, j, plane]);
        });
    });
  }

  // Fifteen fact neurons across the cerebrum, spread front to back.
  const candidates = nodes
    .map((_, i) => i)
    .filter(i => nodes[i].z !== 2 && nodes[i].region === 0)
    .sort((p, q) => nodes[p].x - nodes[q].x);
  const factNodes: number[] = [];
  for (let i = 0; i < FACT_NEURONS; i++) {
    factNodes.push(candidates[Math.floor(((i + 0.4) * candidates.length) / FACT_NEURONS)]);
  }

  return { nodes, links, factNodes };
}

export const BB_FIELD: Field = buildField();
